import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { cn, formatNumber } from "@/lib/utils";
import { LineGraph } from "@/components/ui/shared/graphs";
import { useEffect } from "react";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import { useStats } from "@/hooks/use-stats";
import { Link } from "@tanstack/react-router";
import {
  Banknote,
  ClipboardList,
  ContactRoundIcon,
  ShoppingBag,
} from "lucide-react";
import { Package, Truck, Layers, ArrowLeftRight, Undo2 } from "lucide-react";
import { computeGrossMargin } from "@/store/StockBatch";
import DataPlaceholder from "@/components/ui/shared/data-placeholder";
import StatisticsForm from "@/components/ui/shared/statistics-form";

export const Route = createFileRoute("/(dasboard)/inventory/")({
  component: DashboardPage,
});

const menu = [
  { name: "Products", href: "/products/products", icon: Package },
  {
    name: "Suppliers",
    href: "/products/suppliers",
    icon: Truck,
    requiredRoles: ["shop_owner", "manager"],
  },
  { name: "Inventory Items", href: "/inventory/stock-batchs", icon: Layers },
  { name: "Orders", href: "/inventory/warehouses", icon: ShoppingBag },
  {
    name: "Stock transfers",
    href: "/inventory/stock-transfers",
    icon: ArrowLeftRight,
  },
  { name: "Sale returns", href: "/orders/returns", icon: ContactRoundIcon },
  { name: "Supply returns", href: "/inventory/supply-returns", icon: Undo2 },
];

export const template = [
  {
    label: "Total supply cost",
    key: "total_supply_cost",
    value: 0,
    formatAmount: true,
    color: "text-blue-600",
    bg: "bg-blue-100/70",
    icon: ClipboardList,
  },
  {
    label: "Total sales volume",
    key: "total_sales_volume",
    value: 0,
    formatAmount: true,
    color: "text-gray-600",
    bg: "bg-gray-100/70",
    icon: ShoppingBag,
  },
  {
    label: "Total supply returns",
    key: "total_supply_returns",
    value: 0,
    formatAmount: true,
    color: "text-red-600",
    bg: "bg-red-100/70",
    icon: Undo2,
  },
  {
    label: "Total sale returns",
    key: "total_sale_returns",
    value: 0,
    formatAmount: true,
    color: "text-red-600",
    bg: "bg-red-100/70",
    icon: ContactRoundIcon,
  },
  {
    label: "Gross margin",
    key: "gross_margin",
    value: 0,
    formatAmount: true,
    color: "text-secondary-600",
    bg: "bg-secondary-100/70",
    icon: Banknote,
  },
];

function DashboardPage() {
  const store = useAppStore();
  const { t } = useTranslate();
  const { form, stats, setOverview } = useStats({
    template,
  });

  const fetchStats = () => {
    store.stockBatch
      .getStats(form.values.start_date, form.values.end_date)
      .then((data: any) => {
        setOverview(data, ["total_supply_cost"]);
      });

    store.sale
      .getStats(form.values.start_date, form.values.end_date)
      .then((data: any) => {
        setOverview(data, ["total_sales_volume"]);
      });

    store.supplyReturn
      .getStats(form.values.start_date, form.values.end_date)
      .then((data: any) => {
        setOverview({ total_supply_returns: data.total_returned_amount }, [
          "total_supply_returns",
        ]);
      });

    store.saleReturn
      .getStats(form.values.start_date, form.values.end_date)
      .then((data: any) => {
        setOverview({ total_sale_returns: data.total_returned_amount }, [
          "total_sale_returns",
        ]);
      });

    store.sale.getStats(form.values.start_date, form.values.end_date);
  };

  useEffect(() => {
    if (store.auth.session) {
      fetchStats();
    }
  }, [store.auth.session]);

  useEffect(() => {
    setOverview(
      {
        gross_margin: computeGrossMargin(
          store.sale.stats,
          store.saleReturn.stats,
          store.stockBatch.stats
        ).grossMargin,
      },
      ["gross_margin"]
    );
  }, [store.sale.stats, store.saleReturn.stats, store.stockBatch.stats]);

  const getBatchSerie = (id: string) => {
    return store.stockBatch.stats.series.datasets.filter(
      (item: any) => item.id == id
    );
  };

  const getSaleSerie = (id: string) => {
    return store.sale.stats.series.datasets.filter(
      (item: any) => item.id == id
    );
  };

  const getSupplyReturnSerie = (id: string) => {
    return store.supplyReturn.stats.series.datasets.filter(
      (item: any) => item.id == id
    );
  };

  const getSaleReturnSerie = (id: string) => {
    return store.saleReturn.stats.series.datasets.filter(
      (item: any) => item.id == id
    );
  };

  return (
    <div className="mx-auto">
      <div className="flex items-center  mb-6 gap-2 flex-wrap border-b pb-4 border-gray-300">
        {menu.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className="flex items-center text-gray-800 gap-3 cursor-pointer hover:bg-secondary-50 transition-all duration-300 border border-gray-200 bg-white  py-2 rounded-full px-4"
          >
            <item.icon className="text-secondary-800" />
            <span className="text-sm"> {t(item.name)}</span>
          </Link>
        ))}
      </div>

      <StatisticsForm form={form} fetchStats={fetchStats} />

      <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-5">
        {stats.map((stat: any, index: number) => (
          <Card
            key={index}
            className={cn("rounded-2xl bg-white transition hover:shadow")}
          >
            <CardContent className="flex gap-4 p-4">
              <div>
                <div className={cn("rounded-md bg-white p-2", stat.bg)}>
                  <stat.icon className={cn("h-6 w-6", stat.color)} />
                </div>
              </div>

              <div>
                <p className="text-sm">{t(stat.label)}</p>
                <div className="flex gap-1 font-semibold">
                  <span>
                    {stat.formatAmount
                      ? store.shop.formatAmount(stat.value)
                      : typeof stat.value == "number"
                        ? formatNumber(stat.value)
                        : stat.value}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <section className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-8 w-full">
        <article className="col-span-2">
          <LineGraph
            title={t("Supply cost")}
            colors={getBatchSerie("supply_cost").map((item: any) => item.color)}
            categories={store.stockBatch.stats.series.labels}
            series={getBatchSerie("supply_cost").map((item: any) => ({
              name: t(item.name),
              data: item.data,
            }))}
          />
        </article>
        <article className="col-span-2">
          <LineGraph
            title={t("Sales")}
            colors={getSaleSerie("volume").map((item: any) => item.color)}
            categories={store.sale.stats.series.labels}
            series={getSaleSerie("volume").map((item: any) => ({
              name: t(item.name),
              data: item.data,
            }))}
          />
        </article>
        <article className="col-span-2">
          <LineGraph
            title={t("Sales returns")}
            colors={getSaleReturnSerie("return_volume").map(
              (item: any) => item.color
            )}
            categories={store.saleReturn.stats.series.labels}
            series={getSaleReturnSerie("return_volume").map((item: any) => ({
              name: t(item.name),
              data: item.data,
            }))}
          />
        </article>
        <article className="col-span-2">
          <LineGraph
            title={t("Supply returns")}
            colors={getSupplyReturnSerie("supply_return_volume").map(
              (item: any) => item.color
            )}
            categories={store.supplyReturn.stats.series.labels}
            series={getSupplyReturnSerie("supply_return_volume").map(
              (item: any) => ({
                name: t(item.name),
                data: item.data,
              })
            )}
          />
        </article>
      </section>

      <section className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-gray-200 p-6 bg-white rounded-md">
          <h2 className="font-semibold"> {t("Top products")}</h2>
          {store.sale.stats.top_sold_items.length != 0 ? (
            <ul className="mt-2">
              {store.sale.stats.top_sold_items.map(
                (item: any, index: number) => (
                  <li
                    key={`top_customers_debitors${index}`}
                    className="py-2 flex gap-2 border-b border-gray-200"
                  >
                    <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p>{item.name}</p>
                      <p className="font-semibold text-sm">
                        {store.shop.formatAmount(item.total_volume)}
                      </p>
                    </div>
                  </li>
                )
              )}
            </ul>
          ) : (
            <DataPlaceholder />
          )}
        </div>
        <div className="border border-gray-200 p-6 bg-white rounded-md">
          <h2 className="font-semibold"> {t("Top categories")}</h2>
          {store.sale.stats.top_sold_categories.length != 0 ? (
            <ul className="mt-2">
              {store.sale.stats.top_sold_categories.map(
                (item: any, index: number) => (
                  <li
                    key={`top_suppliers_creditors${index}`}
                    className="py-2 flex gap-2 border-b border-gray-200"
                  >
                    <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p>{item.name}</p>
                      <p className="font-semibold">
                        {store.shop.formatAmount(item.total_volume)}
                      </p>
                    </div>
                  </li>
                )
              )}
            </ul>
          ) : (
            <DataPlaceholder />
          )}
        </div>
        <div className="border border-gray-200 p-6 bg-white rounded-md">
          <h2 className="font-semibold"> {t("Top Customers")}</h2>
          {store.sale.stats.top_customers.length != 0 ? (
            <ul className="mt-2">
              {store.sale.stats.top_customers.map(
                (item: any, index: number) => (
                  <li
                    key={`top_suppliers_debitors${index}`}
                    className="py-2 flex gap-2 border-b border-gray-200"
                  >
                    <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p>{`${item.first_name} ${item.last_name}`}</p>
                      <p className="font-semibold text-sm">
                        {store.shop.formatAmount(item.total_spend)}
                      </p>
                    </div>
                  </li>
                )
              )}
            </ul>
          ) : (
            <DataPlaceholder />
          )}
        </div>
        <div className="border border-gray-200 p-6 bg-white rounded-md">
          <h2 className="font-semibold"> {t("Top Suppliers")}</h2>
          {store.stockBatch.stats.top_suppliers.length != 0 ? (
            <ul className="mt-2">
              {store.stockBatch.stats.top_suppliers.map(
                (item: any, index: number) => (
                  <li
                    key={`top_suppliers_creditors${index}`}
                    className="py-2 flex gap-2 border-b border-gray-200"
                  >
                    <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p>{item.name}</p>
                      <p className="font-semibold">
                        {store.shop.formatAmount(item.supply_value)}
                      </p>
                    </div>
                  </li>
                )
              )}
            </ul>
          ) : (
            <DataPlaceholder />
          )}
        </div>
        <div className="border border-gray-200 p-6 bg-white rounded-md">
          <h2 className="font-semibold"> {t("Top sale returned items")}</h2>
          {store.saleReturn.stats.top_returned_items.length != 0 ? (
            <ul className="mt-2">
              {store.saleReturn.stats.top_returned_items.map(
                (item: any, index: number) => (
                  <li
                    key={`top_customers_debitors${index}`}
                    className="py-2 flex gap-2 border-b border-gray-200"
                  >
                    <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p>{item.name}</p>
                      <p className="font-semibold text-sm">
                        {store.shop.formatAmount(item.volume)}
                      </p>
                    </div>
                  </li>
                )
              )}
            </ul>
          ) : (
            <DataPlaceholder />
          )}
        </div>
        <div className="border border-gray-200 p-6 bg-white rounded-md">
          <h2 className="font-semibold">{t("Top supply returned products")}</h2>
          {store.supplyReturn.stats.top_returned_products.length != 0 ? (
            <ul className="mt-2">
              {store.supplyReturn.stats.top_returned_products.map(
                (item: any, index: number) => (
                  <li
                    key={`top_customers_debitors${index}`}
                    className="py-2 flex gap-2 border-b border-gray-200"
                  >
                    <div className="w-6 h-6 mt-1 rounded-full bg-secondary-100 flex items-center justify-center text-secondary-800 text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p>{item.name}</p>
                      <p className="font-semibold text-sm">
                        {store.shop.formatAmount(item.volume)}
                      </p>
                    </div>
                  </li>
                )
              )}
            </ul>
          ) : (
            <DataPlaceholder />
          )}
        </div>
      </section>
    </div>
  );
}
